import { useAuth } from "@/_core/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useWebSocket, type WSMessage } from "@/hooks/useWebSocket";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getLoginUrl } from "@/const";
import {
  LayoutDashboard,
  Trophy,
  Plus,
  Wallet,
  User,
  Users,
  CreditCard,
  Scale,
  Ticket,
  Gift,
  Gamepad2,
  ClipboardList,
  Settings,
  LogOut,
  BarChart3,
  Megaphone,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Zap,
  Swords,
  Bot,
  Activity,
  ShieldCheck,
  Flag,
  UserCog,
  BrainCircuit,
  Factory,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "wouter";
import NotificationBell from "./NotificationBell";
import { trpc } from "@/lib/trpc";

const userMenuItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/console" },
  { icon: Trophy, label: "Tournaments", path: "/console/tournaments" },
  { icon: Plus, label: "Quick Create", path: "/console/quick-create" },
  { icon: Wallet, label: "Wallet", path: "/console/wallet" },
  { icon: User, label: "Profile", path: "/console/profile" },
  { icon: Users, label: "Referral", path: "/console/referral" },
  { icon: Swords, label: "Challenges", path: "/console/challenges" },
];

const adminMenuItems = [
  { icon: CreditCard, label: "Payouts", path: "/console/withdraw" },
  { icon: Scale, label: "Settles", path: "/console/settles" },
  { icon: Ticket, label: "Coupons", path: "/console/coupons" },
  { icon: Gift, label: "Rewards", path: "/console/rewards" },
  { icon: Gamepad2, label: "GameTypes", path: "/console/gametypes" },
  { icon: ClipboardList, label: "Survey", path: "/console/survey" },
  { icon: BarChart3, label: "Admin Overview", path: "/console/admin-overview" },
  { icon: Megaphone, label: "AI Outreach", path: "/console/outreach" },
  { icon: Factory, label: "AI Forge", path: "/console/ai-forge" },
  { icon: Bot, label: "Agent Management", path: "/console/agents" },
  { icon: Activity, label: "Agent Activity", path: "/console/agent-activity" },
  { icon: ShieldCheck, label: "Agent Approvals", path: "/console/agent-approvals" },
  { icon: Flag, label: "Disputes", path: "/console/disputes" },
  { icon: UserCog, label: "Admin Users", path: "/console/admin-users" },
  { icon: BrainCircuit, label: "AI Models", path: "/console/ai-models" },
];

const bottomItems = [
  { icon: Settings, label: "Settings", path: "/console/settings" },
];

export default function GamePointDashboard({ children }: { children: React.ReactNode }) {
  const { user, loading, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Fetch pending challenge count for badge — must be before early returns to avoid conditional hooks
  const { data: pendingChallenges } = trpc.challenge.pendingCount.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 30000,
  });

  // tRPC utils for cache invalidation on WebSocket events
  const utils = trpc.useUtils();

  // WebSocket live notifications handler
  const handleWSMessage = useCallback((msg: WSMessage) => {
    switch (msg.type) {
      case "challenge_received":
        toast.info(`New Challenge from ${msg.challengerName}!`, {
          description: msg.gameTitle,
          duration: 6000,
        });
        utils.challenge.pendingCount.invalidate();
        utils.challenge.getReceived.invalidate();
        break;
      case "challenge_accepted":
        toast.success(`Challenge Accepted!`, {
          description: `${msg.acceptedByName} accepted "${msg.gameTitle}"`,
          duration: 6000,
        });
        utils.challenge.getSent.invalidate();
        break;
      case "challenge_declined":
        toast(`Challenge Declined`, {
          description: `${msg.declinedByName} declined "${msg.gameTitle}"`,
          duration: 5000,
        });
        utils.challenge.getSent.invalidate();
        break;
      case "challenge_cancelled":
        toast(`Challenge Cancelled`, {
          description: `"${msg.gameTitle}" was cancelled`,
          duration: 5000,
        });
        utils.challenge.getReceived.invalidate();
        break;
      case "challenge_expired":
        toast(`Challenge Expired`, {
          description: `"${msg.gameTitle}" has expired`,
          duration: 5000,
        });
        utils.challenge.getReceived.invalidate();
        utils.challenge.getSent.invalidate();
        break;
      case "notification":
        toast.info(msg.title, {
          description: msg.message,
          duration: 5000,
        });
        break;
    }
  }, [utils]);

  // Connect WebSocket when authenticated
  useWebSocket({
    enabled: !!user,
    onMessage: handleWSMessage,
  });

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Redirect to profile setup if user hasn't completed their profile
  useEffect(() => {
    if (user && user.profileComplete === 0 && location !== "/profile-setup") {
      setLocation("/profile-setup");
    }
  }, [user, location, setLocation]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-neon/30 border-t-teal rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 p-8 max-w-md w-full">
          <div className="w-16 h-16 rounded-2xl bg-neon/10 flex items-center justify-center">
            <Zap className="w-8 h-8 text-neon" />
          </div>
          <h1 className="text-2xl font-bold text-white text-center">Sign in to GamePoint</h1>
          <p className="text-slate-400 text-center text-sm">Access your dashboard, tournaments, and wallet.</p>
          <Button
            onClick={() => { window.location.href = getLoginUrl(); }}
            className="w-full bg-neon hover:bg-neon/90 text-black font-semibold"
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  const isAdmin = user.role === "admin";

  const renderNavItem = (item: typeof userMenuItems[0], isActive: boolean) => {
    const badgeCount = item.path === "/console/challenges" ? (pendingChallenges ?? 0) : 0;
    return (
      <button
        key={item.path}
        onClick={() => setLocation(item.path)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm w-full text-left
          ${isActive
            ? "bg-neon/10 text-neon font-medium"
            : "text-slate-400 hover:text-white hover:bg-white/5"
          }
          ${collapsed ? "justify-center px-2" : ""}
        `}
        title={collapsed ? item.label : undefined}
      >
        <div className="relative shrink-0">
          <item.icon className={`w-[18px] h-[18px] ${isActive ? "text-neon" : ""}`} />
          {badgeCount > 0 && collapsed && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
              {badgeCount > 9 ? "9+" : badgeCount}
            </span>
          )}
        </div>
        {!collapsed && (
          <span className="truncate flex-1">{item.label}</span>
        )}
        {!collapsed && badgeCount > 0 && (
          <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen z-50 flex flex-col border-r border-white/5 bg-[#0c1020] transition-all duration-300
          ${collapsed ? "w-[60px]" : "w-[220px]"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        {/* Logo */}
        <div className={`h-14 flex items-center border-b border-white/5 ${collapsed ? "justify-center px-2" : "px-4"}`}>
          {!collapsed && (
            <Link href="/" className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-neon" />
              <span className="font-bold text-white text-sm tracking-tight">
                Game<span className="text-neon">Point</span>
              </span>
            </Link>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`p-1.5 rounded-md hover:bg-white/5 text-slate-400 hover:text-white transition-colors
              ${collapsed ? "" : "ml-auto"}
            `}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
          {userMenuItems.map(item => renderNavItem(item, location === item.path))}

          {/* Admin separator */}
          {isAdmin && (
            <>
              <div className={`my-3 ${collapsed ? "mx-1" : "mx-2"}`}>
                <div className="h-px bg-white/10" />
                {!collapsed && (
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider mt-2 block px-1">Admin</span>
                )}
              </div>
              {adminMenuItems.map(item => renderNavItem(item, location === item.path))}
            </>
          )}
        </nav>

        {/* Bottom items */}
        <div className="border-t border-white/5 py-2 px-2 space-y-0.5">
          {bottomItems.map(item => renderNavItem(item, location === item.path))}

          {/* User profile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-left hover:bg-white/5 transition-colors ${collapsed ? "justify-center px-2" : ""}`}>
                <Avatar className="h-7 w-7 border border-white/10 shrink-0">
                  <AvatarFallback className="text-[10px] font-medium bg-neon/20 text-neon">
                    {user.name?.charAt(0).toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{user.name || "User"}</p>
                    <p className="text-[10px] text-slate-500 truncate">{user.email || ""}</p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-[#141828] border-white/10">
              <DropdownMenuItem onClick={() => setLocation("/console/profile")} className="text-slate-300 focus:text-white focus:bg-white/5 cursor-pointer">
                <User className="mr-2 h-4 w-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLocation("/console/settings")} className="text-slate-300 focus:text-white focus:bg-white/5 cursor-pointer">
                <Settings className="mr-2 h-4 w-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={logout} className="text-red-400 focus:text-red-300 focus:bg-red-500/10 cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Desktop top bar */}
        <header className="hidden lg:flex h-14 items-center justify-end px-6 border-b border-white/5 bg-[#0c1020] sticky top-0 z-30">
          <NotificationBell />
        </header>

        {/* Mobile header */}
        <header className="lg:hidden h-14 flex items-center px-4 border-b border-white/5 bg-[#0c1020] sticky top-0 z-30">
          <button onClick={() => setMobileOpen(true)} className="p-2 -ml-2 text-slate-400 hover:text-white">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 ml-3">
            <Zap className="w-4 h-4 text-neon" />
            <span className="font-bold text-white text-sm">Game<span className="text-neon">Point</span></span>
          </div>
          <div className="ml-auto">
            <NotificationBell />
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
